import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { fdt, inr } from "@/lib/format";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  PawPrint, User, Stethoscope, Scissors, Home, GraduationCap, Receipt, Lock,
  Syringe, Activity, Calendar, ExternalLink, AlertTriangle, CalendarClock,
} from "lucide-react";

// Desktop → right drawer, mobile → bottom sheet.
function useNarrow() {
  const [narrow, setNarrow] = useState(typeof window !== "undefined" && window.innerWidth < 640);
  useEffect(() => {
    const on = () => setNarrow(window.innerWidth < 640);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return narrow;
}

const VERT_META = {
  vet: { icon: Stethoscope, label: "Veterinary" },
  grooming: { icon: Scissors, label: "Grooming" },
  boarding: { icon: Home, label: "Boarding" },
  training: { icon: GraduationCap, label: "Training" },
  daycare: { icon: Receipt, label: "Reception" },
  reception: { icon: Receipt, label: "Reception" },
};

function Line({ children, testid }) {
  return <div className="text-sm text-pawkt-text flex items-center justify-between gap-3 py-1" data-testid={testid}>{children}</div>;
}

function Restricted({ reason, note }) {
  const map = {
    module_disabled: "This module is disabled for your business.",
    restricted: "You don't have permission to view this.",
    clinical_restricted: note || "Clinical details require veterinary clinical access.",
    no_pet: "No pet linked to this record.",
  };
  return (
    <div className="flex items-start gap-2 rounded-lg bg-pawkt-elevated px-3 py-2.5 text-xs text-pawkt-text-secondary" data-testid="ctx-restricted">
      <Lock className="h-3.5 w-3.5 mt-0.5 shrink-0" />
      <span>{map[reason] || "Not available."}</span>
    </div>
  );
}

function VetSnap({ s }) {
  if (!s.available && s.reason) return <Restricted reason={s.reason} />;
  return (
    <div className="space-y-2" data-testid="ctx-snap-vet">
      {s.reason === "clinical_restricted" && <Restricted reason="clinical_restricted" note={s.note} />}
      <div className="text-xs font-bold uppercase tracking-wide text-pawkt-text-secondary flex items-center gap-1.5"><Syringe className="h-3.5 w-3.5" /> Vaccinations</div>
      {(s.vaccinations || []).length ? (s.vaccinations).map((v, i) => (
        <Line key={i} testid={`ctx-vax-${i}`}><span>{v.vaccine || v.vaccine_type || v.name || "Vaccine"}</span><span className="text-pawkt-text-secondary text-xs">{v.next_due_date ? `Next ${v.next_due_date}` : (v.administered_date || "")}</span></Line>
      )) : <div className="text-xs text-pawkt-text-disabled">No vaccinations recorded.</div>}
      {s.encounters && (
        <>
          <div className="mt-2 text-xs font-bold uppercase tracking-wide text-pawkt-text-secondary flex items-center gap-1.5"><Stethoscope className="h-3.5 w-3.5" /> Recent visits</div>
          {s.encounters.length ? s.encounters.map((e, i) => (
            <Line key={i} testid={`ctx-enc-${i}`}><span>{e.encounter_type || "Consultation"}</span><span className="text-pawkt-text-secondary text-xs">{e.visit_date || ""}</span></Line>
          )) : <div className="text-xs text-pawkt-text-disabled">No visits recorded.</div>}
        </>
      )}
      {s.reason === "clinical_restricted" && typeof s.visit_count === "number" && (
        <div className="text-xs text-pawkt-text-secondary">{s.visit_count} visit(s) on record.</div>
      )}
    </div>
  );
}

function GroomSnap({ s }) {
  if (!s.available && s.reason) return <Restricted reason={s.reason} />;
  const p = s.profile || {};
  return (
    <div className="space-y-2" data-testid="ctx-snap-grooming">
      {(p.coat_type || p.preferred_style || p.notes) ? (
        <div className="rounded-lg bg-pawkt-canvas border border-pawkt-border p-2.5 text-xs text-pawkt-text">
          {p.preferred_style && <div><span className="text-pawkt-text-secondary">Style: </span>{p.preferred_style}</div>}
          {p.coat_type && <div><span className="text-pawkt-text-secondary">Coat: </span>{p.coat_type}</div>}
          {p.notes && <div className="mt-1 text-pawkt-text-secondary">{p.notes}</div>}
        </div>
      ) : <div className="text-xs text-pawkt-text-disabled">No grooming profile yet.</div>}
      <div className="text-xs font-bold uppercase tracking-wide text-pawkt-text-secondary">Recent grooming</div>
      {(s.history || []).length ? s.history.map((t, i) => (
        <Line key={i} testid={`ctx-groom-${i}`}><span>{t.service_name || "Grooming"}</span><span className="text-pawkt-text-secondary text-xs capitalize">{(t.status || "").replace("_", " ")}</span></Line>
      )) : <div className="text-xs text-pawkt-text-disabled">No grooming history.</div>}
    </div>
  );
}

function BoardSnap({ s }) {
  if (!s.available && s.reason) return <Restricted reason={s.reason} />;
  return (
    <div className="space-y-2" data-testid="ctx-snap-boarding">
      <div className="text-xs font-bold uppercase tracking-wide text-pawkt-text-secondary">Recent stays</div>
      {(s.history || []).length ? s.history.map((b, i) => (
        <Line key={i} testid={`ctx-board-${i}`}><span>{b.check_in ? fdt(b.check_in) : "Stay"}</span><span className="text-pawkt-text-secondary text-xs capitalize">{(b.status || "").replace("_", " ")}</span></Line>
      )) : <div className="text-xs text-pawkt-text-disabled">No boarding history.</div>}
    </div>
  );
}

function TrainSnap({ s }) {
  if (!s.available && s.reason) return <Restricted reason={s.reason} />;
  return (
    <div className="space-y-2" data-testid="ctx-snap-training">
      <div className="text-xs font-bold uppercase tracking-wide text-pawkt-text-secondary">Packages</div>
      {(s.packages || []).length ? s.packages.map((p, i) => (
        <Line key={i} testid={`ctx-pkg-${i}`}><span>{p.name || p.goal || "Package"}</span><span className="text-pawkt-text-secondary text-xs capitalize">{(p.status || "").replace("_", " ")}</span></Line>
      )) : <div className="text-xs text-pawkt-text-disabled">No training packages.</div>}
      <div className="mt-2 text-xs font-bold uppercase tracking-wide text-pawkt-text-secondary">Recent sessions</div>
      {(s.sessions || []).length ? s.sessions.slice(0, 5).map((se, i) => (
        <Line key={i} testid={`ctx-sess-${i}`}><span>{se.scheduled_at ? fdt(se.scheduled_at) : "Session"}</span><span className="text-pawkt-text-secondary text-xs capitalize">{(se.status || "").replace("_", " ")}</span></Line>
      )) : <div className="text-xs text-pawkt-text-disabled">No sessions.</div>}
    </div>
  );
}

function ReceptionSnap({ s }) {
  const c = s.commercial || {};
  return (
    <div className="space-y-2" data-testid="ctx-snap-reception">
      <div className="text-xs font-bold uppercase tracking-wide text-pawkt-text-secondary flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" /> Upcoming bookings</div>
      {(s.upcoming_bookings || []).length ? s.upcoming_bookings.map((b, i) => (
        <Line key={i} testid={`ctx-upcoming-${i}`}><span>{b.service_name || "Service"}</span><span className="text-pawkt-text-secondary text-xs">{b.start_time ? fdt(b.start_time) : ""}</span></Line>
      )) : <div className="text-xs text-pawkt-text-disabled">No upcoming bookings.</div>}
      {c.available ? (
        <div className="mt-2 rounded-lg bg-pawkt-canvas border border-pawkt-border p-2.5" data-testid="ctx-commercial">
          <div className="text-xs text-pawkt-text-secondary">Outstanding balance</div>
          <div className={`font-display text-lg font-extrabold ${c.outstanding_total > 0 ? "text-pawkt-emergency" : "text-pawkt-text"}`}>{inr(c.outstanding_total || 0)}</div>
          <div className="text-[11px] text-pawkt-text-disabled">{c.outstanding_count || 0} unpaid invoice(s)</div>
        </div>
      ) : <Restricted reason="restricted" />}
    </div>
  );
}

const SNAP = { vet: VetSnap, grooming: GroomSnap, boarding: BoardSnap, training: TrainSnap, daycare: ReceptionSnap, reception: ReceptionSnap };

export function ContextPanel({ open, onOpenChange, customerId, petId, vertical = "reception", title }) {
  const narrow = useNarrow();
  const navigate = useNavigate();
  const meta = VERT_META[vertical] || VERT_META.reception;

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["context-panel", customerId, petId, vertical],
    enabled: !!open && (!!customerId || !!petId),
    queryFn: async () => (await api.get("/context/panel", { params: { customer_id: customerId || undefined, pet_id: petId || undefined, vertical } })).data,
  });

  const Snap = SNAP[vertical] || ReceptionSnap;
  const cust = data?.customer;
  const pet = data?.pet;
  const ctx = data?.context;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side={narrow ? "bottom" : "right"} className={`bg-pawkt-canvas p-0 overflow-y-auto ${narrow ? "max-h-[88vh] rounded-t-3xl" : "w-full sm:max-w-md"}`} data-testid="context-panel">
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-pawkt-border bg-white sticky top-0 z-10">
          <div className="flex items-center gap-2">
            <meta.icon className="h-4 w-4 text-pawkt-primary" />
            <SheetTitle className="font-display text-base text-pawkt-text">{title || `${meta.label} context`}</SheetTitle>
          </div>
          <SheetDescription className="text-xs text-pawkt-text-secondary">Customer & pet context for this {meta.label.toLowerCase()} record.</SheetDescription>
        </SheetHeader>

        <div className="p-5 space-y-5">
          {isLoading && <div className="text-sm text-pawkt-text-secondary py-8 text-center" data-testid="ctx-loading">Loading context…</div>}

          {isError && (
            <div className="rounded-lg border border-pawkt-coral bg-pawkt-coral/30 p-4 text-sm text-pawkt-emergency flex items-start gap-2" data-testid="ctx-error">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{apiErr(error)}</span>
            </div>
          )}

          {data && (
            <>
              {/* Identity */}
              <div className="rounded-xl border border-pawkt-border bg-white p-4" data-testid="ctx-identity">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-pawkt-primary-soft flex items-center justify-center overflow-hidden shrink-0">
                    {pet?.photo ? <img src={pet.photo} alt={pet.name} className="h-full w-full object-cover" /> : <PawPrint className="h-6 w-6 text-pawkt-primary" />}
                  </div>
                  <div className="min-w-0">
                    <div className="font-display text-base font-bold text-pawkt-text truncate" data-testid="ctx-pet-name">{pet?.name || "—"}</div>
                    <div className="text-xs text-pawkt-text-secondary truncate">{[pet?.species, pet?.breed].filter(Boolean).join(" · ") || "Pet"}{pet?.pet_code ? ` · ${pet.pet_code}` : ""}</div>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2 text-sm">
                  <User className="h-3.5 w-3.5 text-pawkt-text-secondary" />
                  <span className="font-semibold text-pawkt-text" data-testid="ctx-cust-name">{cust?.preferred_name || cust?.name}</span>
                  {cust?.mobile && <span className="text-xs text-pawkt-text-secondary">· {cust.mobile}</span>}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 h-1.5 rounded-full bg-pawkt-elevated overflow-hidden">
                    <div className="h-full rounded-full bg-pawkt-primary" style={{ width: `${cust?.profile_completion || 0}%` }} />
                  </div>
                  <span className="text-[11px] font-bold text-pawkt-primary-deep" data-testid="ctx-completion">{cust?.profile_completion || 0}%</span>
                </div>
                {(pet?.flags || []).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5" data-testid="ctx-flags">
                    {pet.flags.map((f) => (
                      <span key={f.label} className="inline-flex items-center gap-1 rounded-full bg-pawkt-coral text-pawkt-emergency px-2.5 py-0.5 text-xs font-bold">{f.label}: {f.value}</span>
                    ))}
                  </div>
                )}
                <div className="mt-3 flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1 text-xs gap-1" data-testid="ctx-view-customer" disabled={!cust?.id} onClick={() => cust?.id && navigate(`/customers/${cust.id}`)}><ExternalLink className="h-3 w-3" /> Customer 360</Button>
                  {(pet?.id || petId) && <Button variant="outline" size="sm" className="flex-1 text-xs gap-1" data-testid="ctx-view-pet" onClick={() => navigate(`/pets/${pet?.id || petId}`)}><ExternalLink className="h-3 w-3" /> Pet 360</Button>}
                </div>
              </div>

              {/* Vertical snapshot */}
              <div className="rounded-xl border border-pawkt-border bg-white p-4">
                <div className="flex items-center gap-2 mb-2.5">
                  <meta.icon className="h-4 w-4 text-pawkt-primary" />
                  <h4 className="font-display text-sm font-bold text-pawkt-text">{meta.label}</h4>
                </div>
                {ctx?.vertical_snapshot && <Snap s={ctx.vertical_snapshot} />}
              </div>

              {/* Since last visit */}
              <div className="rounded-xl border border-pawkt-border bg-white p-4" data-testid="ctx-since">
                <div className="flex items-center gap-2 mb-2.5">
                  <Calendar className="h-4 w-4 text-pawkt-primary" />
                  <h4 className="font-display text-sm font-bold text-pawkt-text">Since last visit</h4>
                </div>
                {(ctx?.since_last_visit || []).length ? (
                  <div className="relative pl-5 space-y-3">
                    <div className="absolute left-[7px] top-1 bottom-1 w-px bg-pawkt-border" />
                    {ctx.since_last_visit.map((e, i) => (
                      <div key={i} className="relative" data-testid={`ctx-tl-${e.kind}`}>
                        <div className={`absolute -left-[15px] top-0.5 h-4 w-4 rounded-full flex items-center justify-center ring-4 ring-white ${e.restricted ? "bg-pawkt-elevated" : "bg-pawkt-primary-soft"}`}>
                          {e.restricted ? <Lock className="h-2.5 w-2.5 text-pawkt-text-secondary" /> : <Activity className="h-2.5 w-2.5 text-pawkt-primary" />}
                        </div>
                        <div className="text-sm font-semibold text-pawkt-text">{e.title}</div>
                        {e.summary && <div className="text-xs text-pawkt-text-secondary">{e.summary}</div>}
                        <div className="text-[11px] text-pawkt-text-disabled">{fdt(e.at)}</div>
                      </div>
                    ))}
                  </div>
                ) : <div className="text-xs text-pawkt-text-disabled" data-testid="ctx-since-empty">No recent activity to show.</div>}
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
